// src/Redux/thunks/extractionThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosExtraction } from "../../../api/axios";
import { EXTRACTION_PATHS } from "../../../api/endpoints";
import Cookies from "js-cookie";
import {
  selectEffectiveUserId,
  selectPrincipalUserId,
  selectActiveSubprofileId,
} from "../../../sous-profile/redux/selectors/sousProfileSelectors";

const secureConsole = {
  log: () => {},
  warn: () => {},
  error: () => {},
  group: () => {},
  groupEnd: () => {},
  debug: () => {},
};
/** 🔧 Helper pour gérer les erreurs */
const toRejection = (err, defaultMsg = "Une erreur est survenue") => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    defaultMsg
  );
};

/** 🔧 Helper pour normaliser la réponse backend */
const normalizeDocumentsResponse = (data, scope = null) => {
  secureConsole.log("[normalizeDocumentsResponse] 📦 Raw data:", data);

  let documents = [];
  let total = 0;

  if (Array.isArray(data)) {
    documents = data;
    total = data.length;
  } else if (data?.data?.documents) {
    documents = data.data.documents;
    total = data.data.total || documents.length;
  } else if (data?.documents) {
    documents = data.documents;
    total = data.total || documents.length;
  } else if (data?.data && Array.isArray(data.data)) {
    documents = data.data;
    total = documents.length;
  }

  secureConsole.log("[normalizeDocumentsResponse] ✅ Normalized:", {
    documentsCount: documents.length,
    total,
  });

  const scopedDocuments = scope ? filterDocumentsByScope(documents, scope) : documents;
  const scopedTotal = scope ? scopedDocuments.length : total;

  return { documents: scopedDocuments, total: scopedTotal };
};

const cleanId = (value) => {
  if (value == null) return null;
  const clean = String(value).trim();
  return clean || null;
};

const idsEqual = (a, b) => {
  const left = cleanId(a);
  const right = cleanId(b);
  return Boolean(left && right && left === right);
};

const getDocumentSubprofileId = (doc) =>
  cleanId(
    doc?.subprofile_id ??
      doc?.subprofileId ??
      doc?.acting_subprofile_id ??
      doc?.actingSubprofileId
  );

const getDocumentUserId = (doc) =>
  cleanId(
    doc?.user_id ??
      doc?.userId ??
      doc?.patient_id ??
      doc?.patientId ??
      doc?.owner_id ??
      doc?.ownerId
  );

const matchesIdentityScope = (doc, scope = {}) => {
  const scopeUserId = cleanId(scope.userId ?? scope.user_id);
  const scopeSubprofileId = cleanId(scope.subprofileId ?? scope.subprofile_id);
  const documentUserId = getDocumentUserId(doc);
  const documentSubprofileId = getDocumentSubprofileId(doc);

  if (scopeSubprofileId) {
    if (documentSubprofileId) {
      return idsEqual(documentSubprofileId, scopeSubprofileId);
    }

    // Compatibilité avec les anciens documents sauvegardés avec user_id = subprofileId.
    return idsEqual(documentUserId, scopeSubprofileId);
  }

  if (documentSubprofileId) return false;
  if (!scopeUserId || !documentUserId) return true;
  return idsEqual(documentUserId, scopeUserId);
};

const filterDocumentsByScope = (documents, scope) => {
  if (!scope?.userId && !scope?.user_id && !scope?.subprofileId && !scope?.subprofile_id) {
    return documents;
  }

  return documents.filter((doc) => matchesIdentityScope(doc, scope));
};

const normalizeScopeInput = (value) => {
  if (value && typeof value === "object") {
    return {
      userId: cleanId(value.userId ?? value.user_id),
      subprofileId: cleanId(value.subprofileId ?? value.subprofile_id),
    };
  }

  return {
    userId: cleanId(value),
    subprofileId: null,
  };
};

const scopeParams = ({ userId, subprofileId }) => ({
  ...(userId ? { user_id: userId } : {}),
  ...(subprofileId ? { subprofile_id: subprofileId } : {}),
});

/** ✅ Helper robuste : récupérer le userId principal depuis Redux ou localStorage */
const resolveUserId = (state) => {
  const fromPrincipalContext = selectPrincipalUserId(state);
  if (fromPrincipalContext) return String(fromPrincipalContext);

  const fromEffectiveContext = selectEffectiveUserId(state);
  if (fromEffectiveContext) return String(fromEffectiveContext);

  const fromRedux =
    state?.auth?.user?.id ||
    state?.users?.me?.id ||
    state?.user?.me?.id ||
    state?.doctors?.currentDoctor?.userId ||
    state?.doctors?.currentDoctor?.id ||
    state?.doctor?.currentDoctor?.userId ||
    state?.doctor?.currentDoctor?.id;

  if (fromRedux) return String(fromRedux);

  // ✅ Fallback localStorage
  const tryParse = (k) => {
    try {
      const v = localStorage.getItem(k) || Cookies.get(k);
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  };

  const lsUser =
    tryParse("user") ||
    tryParse("patient") ||
    tryParse("doctor") ||
    tryParse("currentUser") ||
    tryParse("authUser");

  return lsUser?.id ? String(lsUser.id) : null;
};

export const resolveMedicalRecordScope = ({
  inputUserId = null,
  inputSubprofileId = null,
  principalUserId = null,
  effectiveUserId = null,
  activeSubprofileId = null,
  fallbackUserId = null,
} = {}) => {
  const requestedUserId = cleanId(inputUserId);
  const requestedSubprofileId = cleanId(inputSubprofileId);
  const principalId = cleanId(principalUserId);
  const effectiveId = cleanId(effectiveUserId);
  const activeSubprofile = cleanId(activeSubprofileId);
  const ownIds = new Set(
    [principalId, effectiveId, activeSubprofile].filter(Boolean),
  );
  const isExternalPatient = Boolean(
    requestedUserId && ownIds.size > 0 && !ownIds.has(requestedUserId),
  );
  const scopedSubprofileId =
    requestedSubprofileId || (!isExternalPatient ? activeSubprofile : null);
  let userId = requestedUserId;

  if (scopedSubprofileId && userId === scopedSubprofileId && principalId) {
    userId = principalId;
  }

  userId =
    userId ||
    (scopedSubprofileId ? principalId : effectiveId) ||
    cleanId(fallbackUserId);

  return {
    userId,
    subprofileId: scopedSubprofileId || null,
  };
};

const resolveIdentityScope = (state, inputUserId = null, inputSubprofileId = null) =>
  resolveMedicalRecordScope({
    inputUserId,
    inputSubprofileId,
    principalUserId: selectPrincipalUserId(state),
    effectiveUserId: selectEffectiveUserId(state),
    activeSubprofileId: selectActiveSubprofileId(state),
    fallbackUserId: resolveUserId(state),
  });

/* ========== ✅ FETCH USER STORAGE - MODIFIÉ POUR ACCEPTER userId ========== */
export const fetchUserStorage = createAsyncThunk(
  "extraction/fetchUserStorage",
  async (scopeArg = null, { rejectWithValue, getState }) => {
    try {
      const requestedScope = normalizeScopeInput(scopeArg);
      const scope = resolveIdentityScope(
        getState(),
        requestedScope.userId,
        requestedScope.subprofileId
      );

      if (!scope.userId) {
        secureConsole.error("[Thunk] ❌ Missing userId -> avoid 400");
        return rejectWithValue("Missing userId (cannot fetch user storage)");
      }

      secureConsole.log("[Thunk] 📊 Using authenticated user scope");

      const response = await axiosExtraction.get(EXTRACTION_PATHS.USER_STORAGE, {
        params: scopeParams(scope),
      });

      secureConsole.log("[Thunk] ✅ Storage response:", response.data);

      const storageData = response.data?.data || response.data;
      return storageData;
    } catch (error) {
      secureConsole.error("[Thunk] ❌ Error fetching storage:", error);
      return rejectWithValue(toRejection(error, "Failed to fetch storage"));
    }
  }
);

/* ========== 🔄 RECALCULATE USER STORAGE - MODIFIÉ POUR ACCEPTER userId ========== */
export const recalculateUserStorage = createAsyncThunk(
  "extraction/recalculateStorage",
  async (scopeArg = null, { rejectWithValue, getState }) => {
    try {
      const requestedScope = normalizeScopeInput(scopeArg);
      const scope = resolveIdentityScope(
        getState(),
        requestedScope.userId,
        requestedScope.subprofileId
      );

      if (!scope.userId) {
        secureConsole.error("[Thunk] ❌ Missing userId -> avoid 400");
        return rejectWithValue("Missing userId (cannot recalculate storage)");
      }

      secureConsole.log("[Thunk] 🔄 Using authenticated user scope");

      const response = await axiosExtraction.post(
        EXTRACTION_PATHS.RECALCULATE_STORAGE,
        null,
        { params: scopeParams(scope) }
      );

      secureConsole.log("[Thunk] ✅ Storage recalculated:", response.data);

      const storageData = response.data?.data || response.data;
      return storageData;
    } catch (error) {
      secureConsole.error("[Thunk] ❌ Error recalculating storage:", error);
      return rejectWithValue(toRejection(error, "Failed to recalculate storage"));
    }
  }
);

/* ========== ✅ PROCESS DOCUMENT + AUTO-REFRESH STORAGE ========== */
export const processDocument = createAsyncThunk(
  "extraction/processDocument",
  async (
    { file, device = "auto", redactionMethod = "blackout", userId = null, subprofileId = null },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const scope = resolveIdentityScope(getState(), userId, subprofileId);
      if (!scope.userId) {
        return rejectWithValue("Missing userId (cannot process document)");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", scope.userId);
      if (scope.subprofileId) formData.append("subprofile_id", scope.subprofileId);
      formData.append("device", device);
      formData.append("redaction_method", redactionMethod);

      secureConsole.group("[processDocument] 📤 UPLOAD REQUEST");
      secureConsole.log("File:", file.name);
      secureConsole.log("Device:", device);
      secureConsole.groupEnd();

      const { data: uploadResponse } = await axiosExtraction.post(
        EXTRACTION_PATHS.PROCESS,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Guards run before Nest parses multipart fields, so the target
          // patient must also be available in the query string.
          params: { patient_id: scope.userId },
          timeout: 300000,
        }
      );

      secureConsole.log("[processDocument] ✅ Upload response:", uploadResponse);

      const documentId =
        uploadResponse?.document_id ||
        uploadResponse?._id ||
        uploadResponse?.id ||
        uploadResponse?.data?.document_id ||
        uploadResponse?.data?._id;

      const collection =
        uploadResponse?.collection || uploadResponse?.data?.collection || "bilan";

      if (documentId && collection) {
        secureConsole.log("[processDocument] 🔍 Fetching from MongoDB:", documentId);

        try {
          const documentUrl = EXTRACTION_PATHS.DOCUMENT_BY_ID(collection, documentId);
          const { data: mongoDocument } = await axiosExtraction.get(documentUrl, {
            params: scopeParams(scope),
          });

          secureConsole.log("[processDocument] ✅ MongoDB document:", mongoDocument);

          // ✅ AUTO-REFRESH STORAGE
          dispatch(fetchUserStorage(scope));

          return {
            ...mongoDocument,
            collection: mongoDocument?.collection || collection,
            document_id: mongoDocument?.document_id || documentId,
          };
        } catch (fetchError) {
          secureConsole.error("[processDocument] ❌ MongoDB fetch failed:", fetchError);
          dispatch(fetchUserStorage(scope));
          return uploadResponse;
        }
      }

      dispatch(fetchUserStorage(scope));
      return uploadResponse;
    } catch (err) {
      secureConsole.error("[processDocument] ❌ Upload error:", err);
      const responseData = err?.response?.data;
      const detail = responseData?.detail ?? responseData;
      return rejectWithValue({
        message:
          (typeof detail === "string" ? detail : detail?.message) ||
          responseData?.message ||
          err?.message ||
          "Erreur lors du traitement du document",
        status: err?.response?.status || err?.status || null,
        detail:
          detail && typeof detail === "object" && !Array.isArray(detail)
            ? detail
            : null,
      });
    }
  }
);

/* ========== ✅ FETCH STATISTICS - MODIFIÉ POUR ACCEPTER userId ========== */
export const fetchStatistics = createAsyncThunk(
  "extraction/fetchStatistics",
  async (scopeArg = null, { rejectWithValue, getState }) => {
    try {
      const requestedScope = normalizeScopeInput(scopeArg);
      const scope = resolveIdentityScope(
        getState(),
        requestedScope.userId,
        requestedScope.subprofileId
      );

      const params = new URLSearchParams(scopeParams(scope));

      const url = params.toString()
        ? `${EXTRACTION_PATHS.STATISTICS}?${params.toString()}`
        : EXTRACTION_PATHS.STATISTICS;

      secureConsole.log("[Thunk] 📊 Fetching statistics");

      const { data } = await axiosExtraction.get(url);
      
      secureConsole.log("[Thunk] ✅ Statistics response:", data);
      
      return data?.data || data;
    } catch (err) {
      secureConsole.error("[Thunk] ❌ Error fetching statistics:", err);
      return rejectWithValue(
        toRejection(err, "Erreur lors de la récupération des statistiques")
      );
    }
  }
);

/* ========== ✅ FETCH MY DOCUMENTS - MODIFIÉ POUR ACCEPTER userId ========== */
export const fetchMyDocuments = createAsyncThunk(
  "extraction/fetchMyDocuments",
  async (
    { skip = 0, limit = 50, userId = null, subprofileId = null },
    { rejectWithValue, getState }
  ) => {
    try {
      secureConsole.log("[Thunk] 📄 fetchMyDocuments called with:", { skip, limit, userId });

      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (scope.userId) {
        params.append("user_id", scope.userId);
        secureConsole.log("[Thunk] 📄 Using authenticated user scope");
      }
      if (scope.subprofileId) {
        params.append("subprofile_id", scope.subprofileId);
      }

      const url = `${EXTRACTION_PATHS.MY_DOCUMENTS}?${params.toString()}`;

      secureConsole.log("[Thunk] 📄 Fetching from:", url);

      const { data } = await axiosExtraction.get(url);

      secureConsole.log("[Thunk] ✅ Documents response:", data);

      const normalized = normalizeDocumentsResponse(data, scope);

      return {
        ...normalized,
        success: true,
      };
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR fetching documents:", err);
      return rejectWithValue(
        toRejection(err, "Erreur lors de la récupération de mes documents")
      );
    }
  }
);

/* ========== ✅ FETCH DOCUMENTS FROM COLLECTION - MODIFIÉ ========== */
export const fetchDocuments = createAsyncThunk(
  "extraction/fetchDocuments",
  async (
    { collection, skip = 0, limit = 10, userId = null, subprofileId = null },
    { rejectWithValue, getState }
  ) => {
    try {
      secureConsole.log("[Thunk] 📁 fetchDocuments called with:", { collection, skip, limit, userId });

      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (scope.userId) {
        params.append("user_id", scope.userId);
        secureConsole.log("[Thunk] 📁 Using authenticated user scope");
      }
      if (scope.subprofileId) {
        params.append("subprofile_id", scope.subprofileId);
      }

      const url = `${EXTRACTION_PATHS.DOCUMENTS(collection)}?${params.toString()}`;

      secureConsole.log("[Thunk] 📁 Fetching from:", url);

      const { data } = await axiosExtraction.get(url);
      const normalized = normalizeDocumentsResponse(data, scope);

      return {
        documents: normalized.documents,
        count: normalized.total,
        collection,
        success: true,
      };
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR fetching collection documents:", err);
      return rejectWithValue(
        toRejection(err, "Erreur lors de la récupération des documents")
      );
    }
  }
);

/* ========== FETCH DOCUMENT BY ID ========== */
export const fetchDocumentById = createAsyncThunk(
  "extraction/fetchDocumentById",
  async ({ collection, id, userId = null, subprofileId = null }, { rejectWithValue, getState }) => {
    try {
      secureConsole.log("[Thunk] 🔍 fetchDocumentById:", { collection, id, userId });

      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      let url = EXTRACTION_PATHS.DOCUMENT_BY_ID(collection, id);

      const params = new URLSearchParams(scopeParams(scope));
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const { data } = await axiosExtraction.get(url);
      return data?.data || data;
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR fetching document:", err);
      return rejectWithValue(toRejection(err, "Document non trouvé"));
    }
  }
);

/* ========== ✅ DELETE DOCUMENT + AUTO-REFRESH STORAGE ========== */
export const deleteDocument = createAsyncThunk(
  "extraction/deleteDocument",
  async (
    { collection, id, userId = null, subprofileId = null },
    { rejectWithValue, dispatch, getState }
  ) => {
    secureConsole.group("[deleteDocument] 🗑️ === START ===");
    secureConsole.log("Collection:", collection);
    secureConsole.log("ID:", id);

    try {
      if (!id) {
        const error = "Document ID is required";
        secureConsole.error("[deleteDocument] ❌", error);
        secureConsole.groupEnd();
        return rejectWithValue(error);
      }

      const normalizedCollection = String(collection || "autre")
        .toLowerCase()
        .replace(/-/g, "_")
        .replace(/\s+/g, "_");
      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      secureConsole.log("Normalized collection:", normalizedCollection);

      const url = EXTRACTION_PATHS.DELETE(normalizedCollection, id);
      secureConsole.log("DELETE URL:", url);

      const response = await axiosExtraction.delete(url, {
        params: scopeParams(scope),
      });

      secureConsole.log("Response status:", response.status);
      secureConsole.log("Response data:", response.data);

      // ✅ AUTO-REFRESH STORAGE
      secureConsole.log("[deleteDocument] 🔄 Auto-refreshing storage...");
      dispatch(fetchUserStorage(scope));

      const result = {
        success: true,
        deletedId: id,
        deletedCollection: normalizedCollection,
        message: response.data?.message || "Document deleted",
        ...(response.data?.data || response.data || {}),
      };

      secureConsole.log("✅ DELETE SUCCESS:", result);
      secureConsole.groupEnd();

      return result;
    } catch (err) {
      secureConsole.group("[deleteDocument] ❌ === ERROR ===");
      secureConsole.error("Error:", err);
      secureConsole.error("Response:", err.response);

      let errorMessage = "Failed to delete document";

      if (err.response?.status === 401) {
        errorMessage = "Authentication failed. Please login again.";
      } else if (err.response?.status === 404) {
        errorMessage = "Document not found or already deleted.";
      } else if (err.response?.status === 403) {
        errorMessage = "You don't have permission to delete this document.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      secureConsole.error("Final error message:", errorMessage);
      secureConsole.groupEnd();

      return rejectWithValue(errorMessage);
    }
  }
);

/* ========== ✅ SEARCH DOCUMENTS - MODIFIÉ ========== */
export const searchDocuments = createAsyncThunk(
  "extraction/searchDocuments",
  async (
    { collection, query, field = "nom_fichier", userId = null, subprofileId = null },
    { rejectWithValue, getState }
  ) => {
    try {
      secureConsole.log("[Thunk] 🔍 searchDocuments:", { collection, query, field, userId });

      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const params = new URLSearchParams({ query, field });

      if (scope.userId) {
        params.append("user_id", scope.userId);
        secureConsole.log("[Thunk] 🔍 Using authenticated user scope");
      }
      if (scope.subprofileId) {
        params.append("subprofile_id", scope.subprofileId);
      }

      const url = `${EXTRACTION_PATHS.SEARCH(collection)}?${params.toString()}`;
      
      secureConsole.log("[Thunk] 🔍 Searching:", url);
      
      const { data } = await axiosExtraction.get(url);

      const rawResults = data?.data?.results || data?.results || data?.data || [];
      const results = filterDocumentsByScope(Array.isArray(rawResults) ? rawResults : [], scope);

      return {
        results,
        query,
        collection,
        success: true,
      };
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR searching:", err);
      return rejectWithValue(toRejection(err, "Erreur lors de la recherche"));
    }
  }
);

/* ========== ✅ FETCH ALL RECENT DOCUMENTS ========== */
export const fetchAllRecentDocuments = createAsyncThunk(
  "extraction/fetchAllRecentDocuments",
  async (
    { skip = 0, limit = 10, userId = null, subprofileId = null } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      secureConsole.log("[Thunk] 📅 fetchAllRecentDocuments:", { skip, limit, userId });
      
      const result = await dispatch(
        fetchMyDocuments({ skip, limit, userId, subprofileId })
      ).unwrap();
      
      return result;
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR fetching recent documents:", err);
      return rejectWithValue(
        toRejection(err, "Erreur lors de la récupération des documents récents")
      );
    }
  }
);

/* ========== ✅ FETCH DOCUMENTS BY TYPE - MODIFIÉ ========== */
export const fetchDocumentsByType = createAsyncThunk(
  "extraction/fetchDocumentsByType",
  async (
    { type, skip = 0, limit = 50, userId = null, subprofileId = null },
    { rejectWithValue, getState }
  ) => {
    try {
      secureConsole.log("[Thunk] 📋 fetchDocumentsByType:", { type, skip, limit, userId });

      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (scope.userId) {
        params.append("user_id", scope.userId);
        secureConsole.log("[Thunk] 📋 Using authenticated user scope");
      }
      if (scope.subprofileId) {
        params.append("subprofile_id", scope.subprofileId);
      }

      const url = `${EXTRACTION_PATHS.MY_DOCUMENTS}?${params.toString()}`;
      
      secureConsole.log("[Thunk] 📋 Fetching from:", url);
      
      const { data } = await axiosExtraction.get(url);
      const normalized = normalizeDocumentsResponse(data, scope);

      const filteredDocuments = normalized.documents.filter(
        (doc) => doc.type_document?.toUpperCase() === type?.toUpperCase()
      );

      secureConsole.log("[Thunk] ✅ Filtered documents:", {
        total: normalized.documents.length,
        filtered: filteredDocuments.length,
        type
      });

      return {
        documents: filteredDocuments,
        total: filteredDocuments.length,
        type,
        success: true,
      };
    } catch (err) {
      secureConsole.error("[Thunk] ❌ ERROR fetching documents by type:", err);
      return rejectWithValue(
        toRejection(err, `Erreur lors de la récupération des documents ${type}`)
      );
    }
  }
);

/* ========== HEALTH CHECK ========== */
export const checkExtractionHealth = createAsyncThunk(
  "extraction/checkHealth",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosExtraction.get(EXTRACTION_PATHS.HEALTH);
      return data?.data || data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Service d'extraction indisponible"));
    }
  }
);

/* ========== UPDATE DOCUMENT PARAMETERS ========== */
export const updateDocumentParameters = createAsyncThunk(
  "extraction/updateDocumentParameters",
  async (
    {
      collection,
      id,
      updateData,
      userId = null,
      subprofileId = null,
      version = null,
    },
    { rejectWithValue, getState }
  ) => {
    try {
      if (!id) throw new Error("Document ID is required");
      if (!updateData?.parameters) throw new Error("Parameters are required");

      const normalizedCollection = String(collection || "bilan")
        .toLowerCase()
        .replace(/-/g, "_");
      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const url = EXTRACTION_PATHS.UPDATE_PARAMETERS(normalizedCollection, id);

      const response = await axiosExtraction.put(url, updateData, {
        params: scopeParams(scope),
        headers:
          version != null ? { "X-Resource-Version": String(version) } : undefined,
      });

      const result = {
        success: true,
        updatedId: id,
        updatedCollection: normalizedCollection,
        message: response.data?.message || "Parameters updated successfully",
        data: response.data?.data || response.data,
        history: response.data?.history,
        stats: response.data?.stats,
      };

      return result;
    } catch (err) {
      let errorMessage = "Failed to update parameters";

      if (err.response?.status === 401) {
        errorMessage = "Authentication failed. Please login again.";
      } else if (err.response?.status === 404) {
        errorMessage = "Document not found.";
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || "Invalid data provided.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/* ========== GET DOCUMENT HISTORY ========== */
export const fetchDocumentHistory = createAsyncThunk(
  "extraction/fetchDocumentHistory",
  async ({ collection, id, userId = null, subprofileId = null }, { rejectWithValue, getState }) => {
    try {
      const normalizedCollection = String(collection || "bilan")
        .toLowerCase()
        .replace(/-/g, "_");
      const scope = resolveIdentityScope(getState(), userId, subprofileId);

      const url = EXTRACTION_PATHS.DOCUMENT_HISTORY(normalizedCollection, id);
      const { data } = await axiosExtraction.get(url, {
        params: scopeParams(scope),
      });

      return {
        success: true,
        history: data?.history || data?.data?.history || [],
        total_modifications: data?.total_modifications || 0,
        last_modified: data?.last_modified,
        last_modified_by: data?.last_modified_by,
      };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch document history"));
    }
  }
);


/* ========== ✅ SHARE DOCUMENT ========== */
export const shareDocument = createAsyncThunk(
  "extraction/shareDocument",
  async (
    { documentId, doctorIds = [], expiresInHours = null },
    { rejectWithValue }
  ) => {
    try {
      if (!documentId) throw new Error("documentId est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      // Cast en entier chaque ID numérique — ms-authdoctor stocke des entiers
      // const normalizedDoctorIds = doctorIds.map((id) => {
      //   const str = String(id).replace(/^DR_/i, "").trim();
      //   const asInt = parseInt(str, 10);
      //   return !isNaN(asInt) && String(asInt) === str ? asInt : str;
      // });

      const normalizedDoctorIds = doctorIds.map((id) =>
  String(id ?? "").replace(/^DR_/i, "").trim()
).filter(Boolean);

      const payload = {
        doctorIds: normalizedDoctorIds,
        ...(expiresInHours != null && { expiresInHours }),
      };

      secureConsole.log("[shareDocument] 📤 Payload:", payload);

      const { data } = await axiosExtraction.post(
        EXTRACTION_PATHS.SHARE_DOCUMENT(documentId),
        payload
      );

      secureConsole.log("[shareDocument] ✅ Response:", data);

      return {
        success: true,
        documentId,
        doctorIds: normalizedDoctorIds,
        data: data?.data || data,
        message: data?.message || "Document partagé avec succès",
      };
    } catch (err) {
      secureConsole.error("[shareDocument] ❌ Error:", err);
      return rejectWithValue(toRejection(err, "Erreur lors du partage du document"));
    }
  }
);

/* ========== ✅ SHARE MULTIPLE DOCUMENTS (batch) ========== */
export const shareDocuments = createAsyncThunk(
  "extraction/shareDocuments",
  async (
    { documents = [], doctorIds = [], expiresInHours = null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      if (!documents.length) throw new Error("Au moins un document est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      const results = [];
      const failures = [];

      for (const doc of documents) {
        const documentId = doc?.id || doc?._id;
        if (!documentId) {
          failures.push({ doc, error: "ID de document manquant" });
          continue;
        }

        try {
          const result = await dispatch(
            shareDocument({ documentId, doctorIds, expiresInHours })
          ).unwrap();
          // Sanitize doc: remove functions and other non-serializable values
          const sanitizedDoc = {};
          for (const key in doc) {
            const value = doc[key];
            if (value !== null && value !== undefined && typeof value !== "function") {
              sanitizedDoc[key] = value;
            }
          }
          results.push({ ...result, doc: sanitizedDoc });
        } catch (err) {
          failures.push({ doc, error: err });
        }
      }

      if (results.length === 0) {
        return rejectWithValue(
          failures[0]?.error || "Échec du partage de tous les documents"
        );
      }

      return {
        success: true,
        shared: results,
        failures,
        totalShared: results.length,
        totalFailed: failures.length,
      };
    } catch (err) {
      secureConsole.error("[shareDocuments] ❌ Error:", err);
      return rejectWithValue(toRejection(err, "Erreur lors du partage des documents"));
    }
  }
);

/* ========== ✅ GET DOCUMENT ACCESS LIST ========== */
export const fetchDocumentAccessList = createAsyncThunk(
  "extraction/fetchDocumentAccessList",
  async ({ documentId }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");

      const { data } = await axiosExtraction.get(
        `/sharing/${encodeURIComponent(String(documentId))}/access`
      );

      return {
        success: true,
        documentId,
        accessList: data?.data || data?.accessList || data || [],
      };
    } catch (err) {
      secureConsole.error("[fetchDocumentAccessList] ❌ Error:", err);
      return rejectWithValue(toRejection(err, "Erreur lors de la récupération des accès"));
    }
  }
);

/* ========== ✅ REVOKE DOCUMENT ACCESS ========== */
export const revokeDocumentAccess = createAsyncThunk(
  "extraction/revokeDocumentAccess",
  async ({ documentId, doctorIds = [] }, { rejectWithValue }) => {
    try {
      if (!documentId) throw new Error("documentId est requis");
      if (!doctorIds.length) throw new Error("Au moins un doctorId est requis");

      const { data } = await axiosExtraction.delete(
        `/sharing/${encodeURIComponent(String(documentId))}/access`,
        { data: { doctorIds } }
      );

      return {
        success: true,
        documentId,
        revokedDoctorIds: doctorIds,
        message: data?.message || "Accès révoqué avec succès",
      };
    } catch (err) {
      secureConsole.error("[revokeDocumentAccess] ❌ Error:", err);
      return rejectWithValue(toRejection(err, "Erreur lors de la révocation de l'accès"));
    }
  }
);
